import { useSelector } from 'react-redux';
import { IRootReducerState } from 'store/store';
import { removeGrossAndNetSuffix } from './helpers';
import { useIsMutating } from 'react-query';
import { ReactMutationKeys } from 'services/api/reactMutationKeys';
import { ISelectedConfigurationAnswer } from 'types/ConfigurationQuestion.types';
import { IIntent } from 'types/Intent.types';
import { useGetTaxRate } from 'providers/QueryProvider/hooks';

export interface IPricingItem {
  description: string;
  document_link_code: string;
  question: string;
  type: string;
  value: number;
  custom_amount?: number | null;
  custom_discount?: number | null;
  discount?: number | null;
  discount_percentage?: number;
}

export interface IFormattedPricingItem {
  key: string;
  grossItem: IPricingItem;
  netItem: IPricingItem;
  name: string;
}

export const useCalculateAndFormatPrices = (
  pricing: Record<string, any> | null,
  quantity: number
): [number, number, IFormattedPricingItem[], boolean] => {
  const configurationAnswerPricingItems: IFormattedPricingItem[] =
    useGenerateFormattedPricingItemsFromSelectedAnswers();

  let isPricingValid = true;
  if (!pricing) {
    return [0, 0, [], true];
  }

  const formattedPrices: IFormattedPricingItem[] = [];
  let totalNetPrice = 0;
  let totalGrossPrice = 0;

  for (const pricingKey of Object.keys(pricing)) {
    const pricingItem = pricing[pricingKey];
    if (pricingItem.value === 'Invalid') {
      isPricingValid = false;
      continue;
    }

    if (pricingItem.type.includes('net')) {
      totalNetPrice += pricingItem.value;
    }

    if (pricingItem.type.includes('gross')) {
      totalGrossPrice += pricingItem.value;
    }

    const formattedPricingItem = formattedPrices.find(
      (formattedPrice) =>
        formattedPrice.key === removeGrossAndNetSuffix(pricingKey)
    );

    if (formattedPricingItem) {
      if (pricingItem.type.includes('net')) {
        formattedPricingItem.netItem = pricingItem;
      } else if (pricingItem.type.includes('gross')) {
        formattedPricingItem.grossItem = pricingItem;
      }
    } else {
      const newFormattedPricingItem: IFormattedPricingItem = {
        key: removeGrossAndNetSuffix(pricingKey),
        grossItem: pricingItem.type.includes('gross') ? pricingItem : 0,
        netItem: pricingItem.type.includes('net') ? pricingItem : 0,
        name: pricingItem.name,
      };
      formattedPrices.push(newFormattedPricingItem);
    }
  }

  for (const answerPricingItem of configurationAnswerPricingItems) {
    formattedPrices.push(answerPricingItem);
    totalNetPrice += answerPricingItem.netItem.value;
    totalGrossPrice += answerPricingItem.grossItem.value;
  }

  return [
    totalNetPrice * quantity,
    totalGrossPrice * quantity,
    formattedPrices,
    isPricingValid,
  ];
};

export const useShouldRoundNumbers = () => {
  const shouldRoundNumbers = useSelector(
    (state: IRootReducerState) => state.modelInfo.roundPricing
  );
  return shouldRoundNumbers;
};

export const useIsEditOrAddToShoppingCartRequestInProgress = () => {
  const isAddingToShoppingCart = useIsMutating({
    mutationKey: ReactMutationKeys.ADD_TO_SHOPPING_CART,
  });
  const isEditingShoppingCartItem = useIsMutating({
    mutationKey: ReactMutationKeys.EDIT_SHOPPING_CART_ITEM,
  });

  return isAddingToShoppingCart || isEditingShoppingCartItem;
};

// One pricing line item per selected configuration answer (extra price question).
// Prices are stored excl. VAT and converted to incl. VAT for display, mirroring
// the previous single extra-price-question behaviour.
export const useGenerateFormattedPricingItemsFromSelectedAnswers =
  (): IFormattedPricingItem[] => {
    const { selectedConfigurationAnswers } = useSelector(
      (state: IRootReducerState) => state.submitConfigurationInfo
    );
    const applyVat = useApplyVat();

    return selectedConfigurationAnswers.map(
      (answer: ISelectedConfigurationAnswer): IFormattedPricingItem => {
        const pricingItem: IPricingItem = {
          description: answer.optionLabel,
          document_link_code: '',
          question: answer.optionLabel,
          type: 'extra_price',
          value: applyVat(answer.price),
        };
        return {
          key: `configurationAnswer-${answer.questionSlug}`,
          grossItem: pricingItem,
          netItem: pricingItem,
          name: answer.questionLabel,
        };
      }
    );
  };

// Returns a function that converts an excl-VAT amount into incl-VAT, using the
// tax rate from the URL param first, then falling back to the intent.
export const useApplyVat = () => {
  const intent: IIntent | null = useSelector(
    (state: IRootReducerState) => state.userInfo.intent
  );
  const testTaxRate: number | undefined = useGetTaxRate();
  const taxRate = testTaxRate !== undefined ? testTaxRate : intent?.tax_rate;

  return (amountExclVat: number | undefined): number => {
    if (!amountExclVat || taxRate === undefined) {
      return 0;
    }
    return amountExclVat * (1 + taxRate / 100);
  };
};

export const useGetConfigurationAnswerPriceInclVAT = (
  amountExclVat: number | undefined
) => {
  const applyVat = useApplyVat();
  return applyVat(amountExclVat);
};
